import {
  Injectable,
  computed,
  inject,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  Connection as ApiConnection,
  ObjectStorageAuthType,
  ObjectStorageConnection,
  OAuthCredential,
} from '@app/shared/interfaces';
import { ConnectionService } from '../../shared/services/connection/connection.service';
import { ObjectStorageService } from '../../shared/services/object-storage/object-storage.service';
import { OAuthStorageService } from '../../shared/services/oauth-storage/oauth-storage.service';
import { normalizeConnectionDescription } from '@app/shared/utils/connection-description.utils';
import {
  ConnectionDisplay,
  buildConnectionDisplays,
  enrichObjectStorageWithMetadata,
  indexObjectStorageMetadata,
} from './connection-display.mapper';

/**
 * Owns all state and data loading for the connections list. The component injects
 * this and only renders + dispatches UI dialogs — there is no load orchestration
 * in the component.
 *
 * Follows the projects-table / invites reference pattern (SCRUM-693/756): the
 * project id is a signal, three `resource()`s load the connections, object-storage
 * and OAuth-credential lists, and `linkedSignal` keeps the previously built rows
 * visible while a refetch is in flight. The OAuth-credentials resource only loads
 * when an object-storage connection actually uses OAuth, matching the old
 * conditional fetch. `firstValueFrom` bridges the Observable HttpClient at the
 * data boundary; the component sees only signals.
 *
 * Provided at the component (`providers: [ConnectionsListStore]`) so its lifetime
 * matches the list.
 */
@Injectable()
export class ConnectionsListStore {
  readonly #connectionService = inject(ConnectionService);
  readonly #objectStorageService = inject(ObjectStorageService);
  readonly #oauthStorageService = inject(OAuthStorageService);

  // ── Inputs (driven by the component) ────────────────────────────────────────
  readonly #inputProjectId = signal<number | null>(null);
  readonly #useMockData = signal(false);

  /** Set the project id from the component input. */
  setProjectId(projectId: number | null): void {
    this.#inputProjectId.set(projectId);
  }

  /** Toggle the mock-data mode from the component input. */
  setUseMockData(useMockData: boolean): void {
    this.#useMockData.set(useMockData);
  }

  /**
   * Resolved project id for API calls: the input id when valid, otherwise the
   * app-state id. Consumers always pass `projectId` today, so the app-state
   * fallback is defensive. Returns `null` when no valid id is available, which
   * idle-gates the resources.
   */
  readonly #resolvedProjectId = computed<number | null>(() => {
    const input = this.#inputProjectId();
    if (input != null && input > 0) {
      return input;
    }
    const appState = this.#connectionService.getCurrentProjectId();
    return appState != null && appState > 0 ? appState : null;
  });

  /** The valid project id currently in use, or `null`. */
  readonly projectId = this.#resolvedProjectId;

  // ── Resources ───────────────────────────────────────────────────────────────
  // `getConnections` also populates ConnectionService's shared `connections`
  // signal as a side effect, which other surfaces rely on; using its value here
  // keeps that behavior.
  readonly #connectionsResource = resource<ApiConnection[], number | undefined>({
    params: () => (this.#useMockData() ? undefined : this.#resolvedProjectId() ?? undefined),
    loader: ({ params }) =>
      firstValueFrom(this.#connectionService.getConnections(params!)),
  });

  readonly #objectStorageResource = resource<ObjectStorageConnection[], number | undefined>({
    params: () => (this.#useMockData() ? undefined : this.#resolvedProjectId() ?? undefined),
    loader: ({ params }) =>
      firstValueFrom(this.#objectStorageService.list(params!)),
  });

  readonly #connectionsValue = computed(() =>
    this.#connectionsResource.hasValue() ? this.#connectionsResource.value() : undefined,
  );
  readonly #objectStorageValue = computed(() =>
    this.#objectStorageResource.hasValue() ? this.#objectStorageResource.value() : undefined,
  );

  // OAuth credentials are only needed to render the status/expiry of OAuth
  // object-storage connections, so the resource idles until at least one exists.
  readonly #oauthResource = resource<OAuthCredential[], number | undefined>({
    params: () => {
      const projectId = this.#resolvedProjectId();
      if (this.#useMockData() || projectId == null) {
        return undefined;
      }
      const objectStorage = this.#objectStorageValue();
      const hasOAuth =
        objectStorage?.some((c) => c.authType === ObjectStorageAuthType.OAuth) ?? false;
      return hasOAuth ? projectId : undefined;
    },
    loader: ({ params }) =>
      firstValueFrom(this.#oauthStorageService.getCredentials(params!)),
  });

  readonly #oauthValue = computed(() =>
    this.#oauthResource.hasValue() ? this.#oauthResource.value() : [],
  );

  // ── Public read-only views ──────────────────────────────────────────────────
  readonly #rows = computed<ConnectionDisplay[] | undefined>(() => {
    if (this.#useMockData()) {
      return buildConnectionDisplays(
        ConnectionService.getMockConnections(),
        ConnectionService.getMockObjectStorageConnections(),
        [],
      );
    }
    const connections = this.#connectionsValue();
    const objectStorage = this.#objectStorageValue();
    if (connections === undefined || objectStorage === undefined) {
      // Still loading the first time — let `displayConnections` retain previous.
      return undefined;
    }
    return buildConnectionDisplays(connections, objectStorage, this.#oauthValue());
  });

  /** Display rows; retains the previously built rows while a refetch is in flight. */
  readonly displayConnections = linkedSignal<ConnectionDisplay[] | undefined, ConnectionDisplay[]>({
    source: () => this.#rows(),
    computation: (value, previous) => value ?? previous?.value ?? [],
  });

  readonly count = computed(() => this.displayConnections().length);

  /** `true` while either primary list is fetching (mock data never loads). */
  readonly loading = computed(
    () =>
      !this.#useMockData() &&
      (this.#connectionsResource.isLoading() || this.#objectStorageResource.isLoading()),
  );

  // ── Commands ────────────────────────────────────────────────────────────────
  /** Refetch the connections and object-storage lists with the current params. */
  reload(): void {
    this.#connectionsResource.reload();
    this.#objectStorageResource.reload();
  }

  /**
   * Delete a connection (standard or object-storage), then refetch. Rejects on
   * API error so the caller can decide whether to surface it.
   */
  async deleteConnection(connection: ConnectionDisplay): Promise<void> {
    const projectId = this.#resolvedProjectId();
    const connectionId = connection.originalData.connectionId;
    if (!projectId || !connectionId) {
      throw new Error('Missing project or connection id for delete');
    }

    if (connection.type === 'objectstorage') {
      await firstValueFrom(this.#objectStorageService.delete(projectId, connectionId));
    } else {
      await firstValueFrom(this.#connectionService.deleteConnection(projectId, connectionId));
    }
    this.reload();
  }

  /**
   * Duplicate a standard connection (object-storage / unknown are unsupported),
   * then refetch. Rejects on API error.
   */
  async duplicateConnection(connection: ConnectionDisplay): Promise<void> {
    if (connection.type === 'objectstorage' || connection.type === 'unknown') {
      throw new Error('Duplicate is not supported for this connection type');
    }
    const projectId = this.#resolvedProjectId();
    if (!projectId) {
      throw new Error('Missing project id for duplicate');
    }

    const original = connection.originalData as ApiConnection;
    await firstValueFrom(
      this.#connectionService.createConnection(projectId, {
        projectId,
        name: `${original.name} (Copy)`,
        description: normalizeConnectionDescription(original.description),
        type: original.type,
        config: original.config,
      }),
    );
    this.reload();
  }

  /**
   * Resolve the connection to hand the edit dialog. Standard connections use the
   * row's own data; object-storage connections are fetched fresh via `get()`
   * (the list omits secret fields) and enriched with name/description from the
   * `/connection` row (SCRUM-425). Falls back to the enriched list row if the
   * fetch fails. Returns `null` when the connection can't be resolved.
   */
  async resolveEditableConnection(
    connection: ConnectionDisplay,
  ): Promise<ApiConnection | ObjectStorageConnection | null> {
    if (connection.type !== 'objectstorage') {
      return connection.originalData as ApiConnection;
    }

    const projectId = this.#resolvedProjectId();
    const connectionId = connection.originalData.connectionId;
    if (!projectId || !connectionId) {
      return null;
    }

    const metadataById = indexObjectStorageMetadata(this.#connectionService.connections());
    try {
      const full = await firstValueFrom(
        this.#objectStorageService.get(projectId, connectionId),
      );
      return enrichObjectStorageWithMetadata(full, metadataById);
    } catch {
      const fromList = this.#objectStorageValue()?.find(
        (item) => item.connectionId === connectionId,
      );
      if (fromList) {
        return enrichObjectStorageWithMetadata(fromList, metadataById);
      }
      if ('provider' in connection.originalData) {
        return enrichObjectStorageWithMetadata(
          connection.originalData as ObjectStorageConnection,
          metadataById,
        );
      }
      return null;
    }
  }
}
